import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
} from "@mui/material";

interface Props {
  open: boolean;
  title: string;
  text: React.ReactNode;
  confirmLabel: string;
  pending?: boolean;
  /** Shows a text field, e.g. a note, and passes its value on. */
  inputLabel?: string;
  inputRequired?: boolean;
  danger?: boolean;
  /** Extra content under the text, e.g. a list of what is affected. */
  children?: React.ReactNode;
  /** A second action next to confirm, e.g. "Save anyway" vs "Download". */
  secondaryAction?: { label: string; onClick: () => void };
  onConfirm: (input: string) => void;
  onClose: () => void;
}

/** Confirmation for delete, discard, archive, ownership transfer and the
 * save-conflict question (after AmbassadorConfirmDialog). */
const ConfirmDialog: React.FC<Props> = ({
  open,
  title,
  text,
  confirmLabel,
  pending = false,
  inputLabel,
  inputRequired,
  danger,
  children,
  secondaryAction,
  onConfirm,
  onClose,
}) => {
  const { t } = useTranslation("documentCrafting");
  const [input, setInput] = useState("");
  const missingInput = Boolean(inputLabel && inputRequired && !input.trim());

  return (
    <Dialog
      open={open}
      onClose={pending ? undefined : onClose}
      maxWidth="sm"
      fullWidth
      TransitionProps={{ onExited: () => setInput("") }}
    >
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        {typeof text === "string" ? (
          <Typography variant="body2" sx={{ whiteSpace: "pre-line" }}>
            {text}
          </Typography>
        ) : (
          text
        )}
        {children}
        {inputLabel && (
          <TextField
            label={inputLabel}
            value={input}
            onChange={(event) => setInput(event.target.value)}
            required={inputRequired}
            multiline
            minRows={3}
            fullWidth
            sx={{ mt: 2 }}
            inputProps={{ maxLength: 5000 }}
          />
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2, flexWrap: "wrap", gap: 1 }}>
        <Button onClick={onClose} color="secondary" disabled={pending}>
          {t("common.cancel")}
        </Button>
        {secondaryAction && (
          <Button
            variant="outlined"
            color="secondary"
            disabled={pending}
            onClick={secondaryAction.onClick}
          >
            {secondaryAction.label}
          </Button>
        )}
        <Button
          variant="contained"
          color={danger ? "error" : "primary"}
          disabled={pending || missingInput}
          onClick={() => onConfirm(input.trim())}
        >
          {confirmLabel}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ConfirmDialog;
